'use client';

import clsx from 'clsx';
import { CircleAlert, CircleCheck, CircleX, Info, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Toast as RadixToast } from 'radix-ui';
import { useMemo } from 'react';
import styles from './Toast.module.scss';

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => (
  <RadixToast.Provider duration={5000}>{children}</RadixToast.Provider>
);

export const ToastViewport: React.FC = () => <RadixToast.Viewport className={styles.viewport} />;

export type ToastVariant = 'unset' | 'info' | 'success' | 'warning' | 'error';

interface ToastProps {
  open?: boolean;
  title: string;
  description?: string;
  variant?: ToastVariant;
  viewTransitionName?: string;
  icon?: React.ReactNode;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  open,
  title,
  description,
  variant = 'unset',
  viewTransitionName,
  icon,
  onClose,
}) => {
  const t = useTranslations('components.toast');
  const defaultIcon = useMemo(() => {
    switch (variant) {
      case 'info':
        return <Info aria-hidden="true" />;
      case 'success':
        return <CircleCheck aria-hidden="true" />;
      case 'warning':
        return <CircleAlert aria-hidden="true" />;
      case 'error':
        return <CircleX aria-hidden="true" />;
      default:
        return null;
    }
  }, [variant]);

  return (
    <RadixToast.Root
      className={clsx(styles.root, {
        [styles.info]: variant === 'info',
        [styles.success]: variant === 'success',
        [styles.warning]: variant === 'warning',
        [styles.error]: variant === 'error',
      })}
      style={{ viewTransitionName }}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      {icon || defaultIcon}
      <div className={styles.content}>
        <RadixToast.Title className={styles.title}>{title}</RadixToast.Title>
        {description && (
          <RadixToast.Description asChild>
            <p className={styles.description}>{description}</p>
          </RadixToast.Description>
        )}
      </div>
      <RadixToast.Close className={styles.close} onClick={onClose} title={t('closeToastTitle')}>
        <span className="sr-only">{t('closeToast')}</span>
        <X aria-hidden="true" />
      </RadixToast.Close>
    </RadixToast.Root>
  );
};
